import { InitialLoading } from '@/components/loading/initial';
import { useSecureStorage } from '@/hooks/useSecureStorage';
import { logoutMiddleware } from '@/middleware/logout';
import { healthCheck } from '@/query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { IBinaryConfig } from '..';
import {
  FEED_AUTH_KEY,
  FEED_BINARY_CONFIG,
  FEED_BINARY_CONFIG_DEFAULT,
  FEED_OFFLINE_MODE,
  VITE_FDM_ENV
} from '../../../constants';
import { ReleaseChannelErrorModal, isReleaseChannelErrorResponse } from '../release-channel-modal';

interface IConfigPros {
  onSuccess: () => void;
}

function isDatabaseConnectionError(message: string): boolean {
  const lowerMessage = message.toLowerCase();
  return (
    lowerMessage.includes("can't reach database server") ||
    lowerMessage.includes("can't reach database") ||
    lowerMessage.includes('database server') ||
    lowerMessage.includes('connection refused') ||
    lowerMessage.includes('connect econnrefused') ||
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('network error')
  );
}

export function CheckBinaryConfig({ onSuccess }: IConfigPros) {
  const {
    state: value,
    loaded: configLoaded,
    setState: setValue,
    resetState: removeBinaryConfig
  } = useSecureStorage<IBinaryConfig>(FEED_BINARY_CONFIG, FEED_BINARY_CONFIG_DEFAULT);
  const [releaseChannelError, setReleaseChannelError] = useState<string | null>(null);

  const handleDatabaseConnectionFailure = (source: 'ping' | 'healthcheck' | 'catch') => {
    console.log(`[CheckBinaryConfig] Database connection error from ${source}, forcing logout and clearing config`);

    logoutMiddleware();
    localStorage.removeItem(FEED_AUTH_KEY);
    localStorage.removeItem(FEED_OFFLINE_MODE);

    removeBinaryConfig();

    toast.error(`Não foi possível conectar ao banco de dados.\nPor favor, verifique as configurações de conexão.`, {
      duration: 5000
    });

    window.location.reload();
  };

  async function handleSubmit() {
    const environment = VITE_FDM_ENV === 'local' ? 'development' : VITE_FDM_ENV === 'stage' ? 'staging' : 'production';
    const config = { ...value, environment };

    const ping = await fetch('fdm://internal/config', {
      method: 'POST',
      body: JSON.stringify(config)
    });

    const pingResponse = await ping.json();

    if (ping.status !== 200 && ping.status !== 208) {
      console.log({ pingResponse });
      const errorMessage = pingResponse.message || String(pingResponse);

      if (pingResponse.code === 'database-unreachable') {
        handleDatabaseConnectionFailure('ping');
        return;
      }

      if (isReleaseChannelErrorResponse(pingResponse)) {
        setReleaseChannelError(pingResponse.message || 'Canal de versão incompatível com o banco de dados.');
        return;
      }

      if (isDatabaseConnectionError(errorMessage)) {
        handleDatabaseConnectionFailure('ping');
        return;
      }

      toast.error(`Erro ao iniciar o feed manager: ${errorMessage}`);
      setValue({ error: true } as Partial<IBinaryConfig>);
      return;
    }

    const { data, status } = await healthCheck();

    if (status !== 200) {
      console.log({ healthcheck: data });

      const isDatabaseDown =
        data &&
        ((data as any).database === (false as any) ||
          (data as any).database === 'false' ||
          (data as any).status === 'error');

      if (isDatabaseDown) {
        const errorMessage = (data as any)?.message || 'Erro de conexão com o banco de dados';

        if (isDatabaseConnectionError(errorMessage)) {
          handleDatabaseConnectionFailure('healthcheck');
          return;
        }
      }

      toast.error(
        `Feed manager está instável:\ndb: ${data.database}\nenv: ${data.environment}\nstatus: ${data.status}\nuptime: ${data.uptime}\n version: ${data.version}`
      );
      setValue({ error: true } as Partial<IBinaryConfig>);
      return;
    }

    onSuccess();
  }

  useEffect(() => {
    if (!configLoaded) return;

    handleSubmit().catch((error) => {
      console.error('[CheckBinaryConfig] Error:', error);
      const errorMessage = error?.message || String(error);

      if (isDatabaseConnectionError(errorMessage)) {
        handleDatabaseConnectionFailure('catch');
        return;
      }

      toast.error(`Erro ao iniciar o feed manager: ${errorMessage}`);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded]);

  return (
    <>
      <InitialLoading text="Carregando o feed manager..." />

      <ReleaseChannelErrorModal
        open={Boolean(releaseChannelError)}
        message={releaseChannelError || ''}
        onClose={() => {
          setReleaseChannelError(null);
          setValue({ error: true } as Partial<IBinaryConfig>);
        }}
      />
    </>
  );
}
